import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import React from "react";

import { RobotoButton } from "@/shared/components";
import { WebhookIntegrationRecord } from "@/shared/domain/integrations";
import { useUpdateWebhookIntegrationApiKey } from "@/shared/domain/integrations/hooks/useUpdateWebhookIntegrationApiKey";

import styles from "./FoxgloveIntegrationStepper.module.css";

export interface ProvisionApiKeyContentProps {
  foxgloveIntegration: WebhookIntegrationRecord | null | undefined;
}

export function ProvisionApiKeyContent({
  foxgloveIntegration,
}: ProvisionApiKeyContentProps) {
  const [apiKey, setApiKey] = React.useState("");
  const updateApiKeyMutation = useUpdateWebhookIntegrationApiKey();

  // Determine if we need events.list scope based on webhook_events
  const includeEventCreated =
    foxgloveIntegration?.webhook_events?.includes("event.created") ?? false;

  const handleUpdateApiKey = () => {
    const trimmedApiKey = apiKey.trim();
    if (!foxgloveIntegration?.webhook_endpoint_id || !trimmedApiKey) {
      return;
    }

    updateApiKeyMutation.mutate(
      {
        webhookEndpointId: foxgloveIntegration.webhook_endpoint_id,
        apiKey: trimmedApiKey,
      },
      {
        onSuccess: () => {
          setApiKey("");
        },
      },
    );
  };

  return (
    <>
      <Typography variant="body2" className={styles.marginBottom2}>
        Create a scoped API key in Foxglove to allow Roboto to access your
        recording data.
      </Typography>

      <Box className={styles.marginBottom2}>
        <Typography variant="body2" className={styles.marginBottom1}>
          1. Go to{" "}
          <Link
            href="https://app.foxglove.dev/~/settings/apikeys"
            target="_blank"
            rel="noopener noreferrer"
          >
            Foxglove API Keys
          </Link>
        </Typography>
        <Typography variant="body2" className={styles.marginBottom1}>
          2. Create a new API key with the following scopes:
        </Typography>
        <Box className={classNames(styles.marginLeft2, styles.marginBottom1)}>
          <Typography
            variant="body2"
            component="div"
            className={styles.marginBottom1}
          >
            <Chip
              label="recordings.list"
              size="small"
              color="primary"
              variant="outlined"
              className={classNames(
                styles.foxgloveEventChip,
                styles.foxgloveEventChipInline,
              )}
            />
            to retrieve recording metadata
          </Typography>
          {includeEventCreated && (
            <Typography
              variant="body2"
              component="div"
              className={styles.marginBottom1}
            >
              <Chip
                label="events.list"
                size="small"
                color="primary"
                variant="outlined"
                className={classNames(
                  styles.foxgloveEventChip,
                  styles.foxgloveEventChipInline,
                )}
              />
              to retrieve device event metadata
            </Typography>
          )}
          <Typography
            variant="body2"
            component="div"
            className={styles.marginBottom1}
          >
            <Chip
              label="data.stream"
              size="small"
              color="primary"
              variant="outlined"
              className={classNames(
                styles.foxgloveEventChip,
                styles.foxgloveEventChipInline,
              )}
            />
            to download recording data
          </Typography>
        </Box>
        <Typography variant="body2" className={styles.marginBottom2}>
          3. Copy the API key and paste it below
        </Typography>
      </Box>

      <Box className={styles.marginBottom2}>
        <TextField
          fullWidth
          label="Foxglove API Key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Paste your Foxglove API key here"
          className={styles.marginBottom2}
        />
        <RobotoButton
          variant="contained"
          onClick={handleUpdateApiKey}
          disabled={!apiKey.trim() || updateApiKeyMutation.isPending}
          startIcon={
            updateApiKeyMutation.isPending ? (
              <CircularProgress size={16} />
            ) : null
          }
          eventName="UpdateFoxgloveApiKeyClicked"
        >
          {updateApiKeyMutation.isPending
            ? "Registering..."
            : "Register API Key"}
        </RobotoButton>
      </Box>

      {updateApiKeyMutation.isError && (
        <Alert severity="error" className={styles.marginBottom2}>
          Failed to update API key. Please try again.
        </Alert>
      )}
    </>
  );
}
